import { PaymentAttempt } from '../models/entities/payment-attempt';
import { PaymentItem } from '../models/entities/payment-item';
import { Transaction } from '../models/entities/transactions/transaction';

/**
 * Determines the display amount for a child transaction in deposit/payment contexts.
 * Returns a negative value for inflows (renders green via legfiBalance pipe)
 * and a positive value for outflows like returns/disputes (renders black).
 *
 * Outflow detection signals:
 * - Description starts with 'returned' or 'disputed'
 * - Amount is already negative in DB
 * - Transaction is soft-deleted (deletedAt set)
 * - PaymentAttempt reference has returned flag
 * - It is a node whose children are all returns (see isReturnLine, SD-7465)
 */
export function getChildDisplayAmount(child: Transaction): number {
    return isOutflowLine(child) ? Math.abs(child.amount) : -Math.abs(child.amount);
}

/**
 * Whether a single line represents an outflow (renders black); inflows render green.
 * A line is an outflow when its amount is already negative, or it carries a return
 * marker (see isReturnLine).
 */
function isOutflowLine(child: Transaction): boolean {
    return child.amount < 0 || isReturnLine(child);
}

/**
 * Whether a line carries a definite return/dispute marker. Used both directly (a leaf's
 * own signal) and recursively to decide a parent node's direction.
 *
 * SD-7465: a payer/attempt node carries none of these per-line signals of its own, and its
 * PaymentAttempt.returned flag is an attempt-level, *shared* boolean that stays false for a
 * partial refund (it is only set once no live payment_items remain, and the same attempt is
 * also referenced by the still-live original payment). Such a node instead has child leaves
 * that DO carry the return signal ('Returned:'/'Disputed:'), so derive the node's direction
 * from its children when it has some and every one of them is itself a return. The length
 * guard matters: [].every() is true, so an unloaded/childless node must stay green.
 *
 * A bare negative amount is deliberately NOT a return marker here: on its own it must not
 * promote a whole node to an outflow (an all-adjustment/discount node has no real return
 * leaves and stays green) — only isOutflowLine treats a negative leaf amount as an outflow.
 */
function isReturnLine(child: Transaction): boolean {
    const description = (child.description || '').toLowerCase();

    return description.startsWith('returned') ||
           description.startsWith('disputed') ||
           !!child.deletedAt ||
           !!(child.reference && 'returned' in child.reference && (child.reference as PaymentAttempt).returned) ||
           (!!child.children?.length && child.children.every(isReturnLine));
}

/**
 * Determines the display amount for a payment item.
 * Returns a negative value for normal payments (renders green via legfiBalance pipe)
 * and a positive value for returned items (renders black).
 */
export function getPaymentItemDisplayAmount(item: PaymentItem): number {
    const isReturned = !!item.deletedAt;
    return isReturned ? Math.abs(item.amount) : -Math.abs(item.amount);
}
